import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Inbox, Phone, Pencil, MessageSquare, CalendarDays, List,
  ChevronLeft, ChevronRight, ExternalLink, Trash2,
} from "lucide-react";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { ScheduleDialog } from "./ScheduleDialog";
import { SmsDialog } from "./SmsDialog";
import {
  APPT_STATUSES, apptStatusColors, agreementLink, buildScheduledSms,
  firstName, fmtDateOnly, fmtTime12, gcalLink,
} from "./leadUtils";
import { useConfig } from "../../hooks/useConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0a0a0a] border-zinc-800 text-white h-10 rounded-sm placeholder:text-zinc-600";

const reminderChipColor = {
  Scheduled: "text-amber-400 border-amber-500/40",
  Sent: "text-green-400 border-green-500/40",
  Failed: "text-red-400 border-red-500/40",
  Cancelled: "text-zinc-500 border-zinc-700",
};

const CalendarView = ({ appts, onSelect }) => {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const byDate = useMemo(() => {
    const m = {};
    appts.forEach((a) => { (m[a.date] = m[a.date] || []).push(a); });
    return m;
  }, [appts]);
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const offset = first.getDay();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const pad = (n) => String(n).padStart(2, "0");
  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="appointments-calendar">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="calendar-prev" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="p-2 border border-zinc-800 rounded-sm text-zinc-400 hover:text-white"><ChevronLeft className="h-4 w-4" /></button>
        <div className="font-heading font-semibold text-white">{month.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div>
        <button data-testid="calendar-next" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="p-2 border border-zinc-800 rounded-sm text-zinc-400 hover:text-white"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-zinc-500 text-xs mb-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) return <div key={`empty-${i}`} />;
          const dateStr = `${month.getFullYear()}-${pad(month.getMonth() + 1)}-${pad(day)}`;
          const dayAppts = (byDate[dateStr] || []).filter((a) => a.status !== "Cancelled");
          return (
            <div key={dateStr} className={`min-h-[84px] border rounded-sm p-1.5 ${dateStr === todayStr ? "border-blue-500/60 bg-blue-500/5" : "border-zinc-800 bg-[#0d0d0d]"}`}>
              <div className="text-zinc-500 text-xs mb-1">{day}</div>
              {dayAppts.slice(0, 3).map((a) => (
                <button key={a.appointment_id} onClick={() => onSelect(a)} className={`w-full text-left rounded-sm px-1 py-0.5 text-[10px] mb-0.5 truncate border ${a.gcal ? "bg-green-600/20 border-green-500/40 text-green-300 hover:bg-green-600/40" : "bg-blue-600/20 border-blue-500/40 text-blue-300 hover:bg-blue-600/40"}`}>
                  {a.all_day ? "All day" : fmtTime12(a.start_time)} {a.name}
                </button>
              ))}
              {dayAppts.length > 3 && <div className="text-zinc-600 text-[10px]">+{dayAppts.length - 3} more</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const AppointmentsTab = ({ onAuthError }) => {
  const config = useConfig();
  const [appts, setAppts] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("list");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [smsAppt, setSmsAppt] = useState(null);
  const [gcalEvents, setGcalEvents] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [a, r] = await Promise.all([
        axios.get(`${API}/admin/appointments`),
        axios.get(`${API}/admin/appointment-reminders`),
      ]);
      setAppts(a.data);
      setReminders(r.data);
    } catch (err) {
      if (!onAuthError(err)) toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
    axios.get(`${API}/admin/gcal/events`).then((g) => setGcalEvents(g.data)).catch(() => setGcalEvents([]));
  }, [onAuthError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const remindersByAppt = useMemo(() => {
    const m = {};
    reminders.forEach((r) => { (m[r.appointment_id] = m[r.appointment_id] || []).push(r); });
    return m;
  }, [reminders]);

  const setStatus = async (appt, status) => {
    try {
      await axios.patch(`${API}/admin/appointments/${appt.appointment_id}`, { status, origin_url: window.location.origin });
      toast.success(`Appointment ${status.toLowerCase()}`);
      fetchAll();
    } catch (err) {
      if (!onAuthError(err)) toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const logSms = (appt) => async (message) => {
    try {
      await axios.post(`${API}/admin/appointments/${appt.appointment_id}/log-sms`, { message, channel: "sms" });
      const conf = (remindersByAppt[appt.appointment_id] || []).find((r) => r.kind === "confirmation" && r.status === "Scheduled");
      if (conf) await axios.post(`${API}/admin/appointment-reminders/${conf.reminder_id}/mark-sent`);
      toast.success("Confirmation logged");
      fetchAll();
    } catch {
      toast.error("Could not log message");
    }
  };

  const decideApproval = async (appt, req, status) => {
    try {
      await axios.patch(`${API}/admin/appointments/${appt.appointment_id}/approvals/${req.id}`, { status });
      toast.success(`Additional work ${status.toLowerCase()}`);
      fetchAll();
    } catch (err) {
      if (!onAuthError(err)) toast.error("Could not update approval");
    }
  };

  const removeAppt = async (appt) => {
    if (!window.confirm(`Delete appointment for ${appt.name} on ${appt.date}?`)) return;
    try {
      await axios.delete(`${API}/admin/appointments/${appt.appointment_id}`);
      toast.success("Appointment deleted");
      fetchAll();
    } catch (err) {
      if (!onAuthError(err)) toast.error("Delete failed");
    }
  };

  const filtered = appts.filter((a) => {
    if (statusFilter !== "All" && a.status !== statusFilter) return false;
    if (dateFilter && a.date !== dateFilter) return false;
    const q = search.toLowerCase();
    if (q && !`${a.name} ${a.phone} ${a.service_type} ${a.technician_name || ""}`.toLowerCase().includes(q)) return false;
    return true;
  });

  const crmKeys = useMemo(() => new Set(appts.map((a) => `${a.date}|${a.start_time}`)), [appts]);
  const gcalRows = gcalEvents
    .filter((e) => statusFilter === "All")
    .filter((e) => !crmKeys.has(`${e.date}|${e.start_time}`))
    .filter((e) => (dateFilter ? e.date === dateFilter : true))
    .filter((e) => {
      const q = search.toLowerCase();
      return !q || `${e.summary} ${e.location || ""} ${e.description || ""}`.toLowerCase().includes(q);
    })
    .map((e) => ({
      ...e, gcal: true, appointment_id: `gcal-${e.id}`, name: e.summary,
      start_time: e.start_time || "00:00", status: "Google",
    }));
  const combined = [...filtered, ...gcalRows].sort((a, b) =>
    a.date === b.date ? (b.start_time || "").localeCompare(a.start_time || "") : b.date.localeCompare(a.date));

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;

  return (
    <div data-testid="appointments-tab">
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <Input data-testid="appointments-search" className={`${inputCls} max-w-xs`} placeholder="Search name, service, tech..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <select data-testid="appointments-filter-status" className="h-10 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-3 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="All">Status: All</option>
          {APPT_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="appointments-filter-date" className={`h-10 inline-flex items-center gap-2 bg-[#0a0a0a] border rounded-sm px-3 text-sm ${dateFilter ? "border-blue-500/50 text-white" : "border-zinc-800 text-zinc-400"}`}>
              <CalendarDays className="h-4 w-4" />
              {dateFilter ? fmtDateOnly(dateFilter) : "Any date"}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-[#121212] border-zinc-800" align="start">
            <Calendar
              mode="single"
              selected={dateFilter ? new Date(`${dateFilter}T00:00:00`) : undefined}
              onSelect={(d) => setDateFilter(d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "")}
            />
          </PopoverContent>
        </Popover>
        {dateFilter && (
          <button data-testid="appointments-filter-date-clear" onClick={() => setDateFilter("")} className="text-xs text-zinc-500 hover:text-white underline">Clear date</button>
        )}
        <div className="flex gap-1 ml-auto">
          <button data-testid="appointments-view-list" onClick={() => setMode("list")} className={`p-2.5 rounded-sm border ${mode === "list" ? "bg-blue-600 border-blue-600 text-white" : "border-zinc-800 text-zinc-400"}`}><List className="h-4 w-4" /></button>
          <button data-testid="appointments-view-calendar" onClick={() => setMode("calendar")} className={`p-2.5 rounded-sm border ${mode === "calendar" ? "bg-blue-600 border-blue-600 text-white" : "border-zinc-800 text-zinc-400"}`}><CalendarDays className="h-4 w-4" /></button>
        </div>
      </div>

      {mode === "calendar" ? (
        <CalendarView appts={combined} onSelect={(a) => (a.gcal ? window.open(a.html_link, "_blank") : setEditing(a))} />
      ) : combined.length === 0 ? (
        <div data-testid="appointments-empty-state" className="text-center py-24 text-zinc-500">
          <Inbox className="h-12 w-12 mx-auto mb-4 text-zinc-700" />
          No appointments found. Schedule one from a Lead or Customer record.
        </div>
      ) : (
        <div className="space-y-3" data-testid="appointments-list">
          {combined.map((a) => a.gcal ? (
            <div key={a.appointment_id} data-testid={`gcal-row-${a.id}`} className="bg-[#0f1512] border border-green-500/25 rounded-xl p-4 sm:p-5 flex flex-wrap items-center gap-4">
              <div className="min-w-[130px]">
                <div className="text-white font-semibold text-sm">{fmtDateOnly(a.date)}</div>
                <div className="text-green-400 text-sm">{a.all_day ? "All day" : `${fmtTime12(a.start_time)}${a.end_time ? ` – ${fmtTime12(a.end_time)}` : ""}`}</div>
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="font-heading font-semibold text-white">{a.summary}</div>
                {a.location && <div className="text-zinc-500 text-xs truncate">{a.location}</div>}
              </div>
              <span className="text-[10px] font-bold uppercase border border-green-500/40 bg-green-600/15 text-green-300 rounded-sm px-2 py-0.5">Google Calendar</span>
              <a href={a.html_link} target="_blank" rel="noopener noreferrer" className="p-2 border border-zinc-700 rounded-sm text-zinc-400 hover:text-white" title="Open in Google Calendar"><ExternalLink className="h-4 w-4" /></a>
            </div>
          ) : (
            <div key={a.appointment_id} data-testid={`appointment-row-${a.appointment_id}`} className="bg-[#121212] border border-zinc-800 rounded-xl p-4 sm:p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
              <div className="lg:col-span-2">
                <div className="text-white font-semibold text-sm">{fmtDateOnly(a.date)}</div>
                <div className="text-blue-400 text-sm">{fmtTime12(a.start_time)} · {a.duration_minutes || 60} min</div>
              </div>
              <div className="lg:col-span-3">
                <div className="font-heading font-semibold text-white">{a.name}</div>
                <a href={`tel:${a.phone.replace(/[^+\d]/g, "")}`} className="inline-flex items-center gap-1.5 text-blue-400 text-xs"><Phone className="h-3 w-3" /> {a.phone}</a>
                <div className="text-zinc-400 text-xs">{a.service_type}</div>
                {a.address && <div className="text-zinc-600 text-xs truncate">{a.address}</div>}
              </div>
              <div className="lg:col-span-3 text-xs space-y-1">
                {a.technician_name && <div className="text-zinc-300">Tech: {a.technician_name}</div>}
                {a.priority && a.priority !== "Normal" && <div className="text-red-400 font-semibold uppercase">{a.priority} priority</div>}
                <div className={a.agreement_status === "Accepted" ? "text-green-400" : "text-amber-400"}>Agreement {a.agreement_status || "Pending"}</div>
                {(a.approval_requests || []).filter((r) => r.status === "Pending").map((r) => (
                  <div key={r.id} className="border border-amber-500/40 bg-amber-500/10 rounded-sm p-1.5">
                    <div className="text-amber-300">Approval: {r.description}{r.estimated_cost ? ` (~$${r.estimated_cost})` : ""}</div>
                    <div className="flex gap-2 mt-1">
                      <button data-testid={`approval-approve-${r.id}`} onClick={() => decideApproval(a, r, "Approved")} className="text-green-400 font-semibold">Approve</button>
                      <button data-testid={`approval-decline-${r.id}`} onClick={() => decideApproval(a, r, "Declined")} className="text-red-400 font-semibold">Decline</button>
                    </div>
                  </div>
                ))}
                <div className="flex flex-wrap gap-1">
                  {(remindersByAppt[a.appointment_id] || []).filter((r) => r.status !== "Cancelled").map((r) => (
                    <span key={r.reminder_id} title={r.last_error || ""} className={`border rounded-sm px-1.5 py-0.5 text-[10px] ${reminderChipColor[r.status] || "text-zinc-400 border-zinc-700"}`}>
                      {r.kind} · {r.status}
                    </span>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-4 flex items-center gap-2 flex-wrap lg:justify-end">
                <Select value={a.status} onValueChange={(v) => setStatus(a, v)}>
                  <SelectTrigger data-testid={`appointment-status-select-${a.appointment_id}`} className="bg-[#0a0a0a] border-zinc-800 text-white h-9 rounded-sm w-32 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-zinc-800 text-white">
                    {APPT_STATUSES.map((s) => <SelectItem key={s} value={s} className="focus:bg-blue-600/20 focus:text-white">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button data-testid={`appointment-sms-${a.appointment_id}`} onClick={() => setSmsAppt(a)} className="p-2 border border-blue-500/50 rounded-sm text-blue-400 hover:bg-blue-500/10" title="Text confirmation / agreement link"><MessageSquare className="h-4 w-4" /></button>
                <button data-testid={`appointment-edit-${a.appointment_id}`} onClick={() => setEditing(a)} className="p-2 border border-zinc-700 rounded-sm text-zinc-400 hover:text-white" title="Edit / reschedule"><Pencil className="h-4 w-4" /></button>
                <a data-testid={`appointment-gcal-${a.appointment_id}`} href={gcalLink(a)} target="_blank" rel="noopener noreferrer" className="p-2 border border-zinc-700 rounded-sm text-zinc-400 hover:text-white" title="Add to Google Calendar"><ExternalLink className="h-4 w-4" /></a>
                <button data-testid={`appointment-delete-${a.appointment_id}`} onClick={() => removeAppt(a)} className="p-2 border border-zinc-800 rounded-sm text-zinc-500 hover:text-red-400" title="Delete appointment"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ScheduleDialog
          key={`edit-${editing.appointment_id}-${editing.updated_at}`}
          open
          onOpenChange={(v) => !v && setEditing(null)}
          appointment={editing}
          onSaved={() => { setEditing(null); fetchAll(); }}
        />
      )}
      {smsAppt && (
        <SmsDialog
          key={`appt-sms-${smsAppt.appointment_id}`}
          open
          onOpenChange={(v) => !v && setSmsAppt(null)}
          phone={smsAppt.phone}
          title="Send Confirmation / Agreement"
          initialMessage={buildScheduledSms(
            firstName(smsAppt.name), fmtDateOnly(smsAppt.date), fmtTime12(smsAppt.start_time),
            smsAppt.service_type, agreementLink(smsAppt.lead_id), config.booking_url,
          )}
          onLogged={logSms(smsAppt)}
        />
      )}
    </div>
  );
};
